import unfetch from 'isomorphic-unfetch';
import getConfig from 'next/config';
import { NextApiRequest } from 'next';

const {
  publicRuntimeConfig: { ACCOUNTS_SERVICE_HOST },
} = getConfig();

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await unfetch(ACCOUNTS_SERVICE_HOST + url, {
    mode: 'cors',
    cache: 'default',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  return response.json();
}

/**
 * Makes a request passing the cookies from the request (req) to url. This is useful for serverSide requests that
 * require authentication.
 *
 * @param req NextApiRequest
 * @param url Full URL -- must include the host
 * @param options Request options
 * @returns {Promise<any>}
 */
async function serverSideRequest<T>(
  req,
  url: string,
  options?: RequestInit
): Promise<T> {
  const response = await unfetch(ACCOUNTS_SERVICE_HOST + url, {
    mode: 'cors',
    cache: 'default',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Cookie: req.headers.cookie,
    },
    ...options,
  });

  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  return response.json();
}

export interface AccessTokenResponse {
  /** The access token to use for identification and authorization of requests to protected endpoints. */
  access_token: string;
  /** The type of access token issued. */
  token_type: string;
  /** The lifetime (in seconds) for which the access token is valid. */
  expires_in: number;
  /** The long-lived refresh token which can be exchanged for a new access token. */
  refresh_token?: string;
}

/**
 * Login via basic username and password.
 */
async function localAuth(
  email: string,
  password: string
): Promise<AccessTokenResponse> {
  return request('/auth/local', {
    method: 'POST',
    body: JSON.stringify({
      username: email,
      password,
    }),
  });
}

/**
 * Login via Google SSO issued idToken.
 */
async function googleAuth(idToken: string): Promise<AccessTokenResponse> {
  return request('/auth/google', {
    method: 'POST',
    body: JSON.stringify({
      idToken,
    }),
  });
}

/**
 * Login via Facebook SSO issued accessToken.
 */
async function facebookAuth(accessToken: string): Promise<AccessTokenResponse> {
  return request('/auth/facebook', {
    method: 'POST',
    body: JSON.stringify({
      accessToken,
    }),
  });
}

/**
 * Refresh current session and obtain a new access token.
 * @param refToken Refresh token to use for refreshing credentials. Only necessary in stateless environments. (optional)
 * @param serverSideReq Server-side "req" variable used in server-side calls. Leave empty if in client-side.
 */
async function refresh(
  refToken?: string,
  serverSideReq: NextApiRequest = undefined
): Promise<AccessTokenResponse> {
  const url = '/auth/refresh';
  const body = refToken == null ? undefined : { token: refToken };
  const options = {
    method: 'POST',
    body: JSON.stringify(body),
  };
  if (serverSideReq) {
    return serverSideRequest(serverSideReq, url, options);
  }
  return request(url, options);
}

/**
 * Logout of the current session.
 * @param refToken Refresh token to use for destroying session. Only necessary in stateless environments. (optional)
 */
async function logout(refToken?: string): Promise<void> {
  const body = refToken == null ? undefined : { token: refToken };

  return request('/auth/logout', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export default {
  localAuth,
  googleAuth,
  facebookAuth,
  refresh,
  logout,
};
